/**
 * Formateo de dinero y catálogo de monedas (multi-moneda, hito 1).
 *
 * Hasta aquí cada pantalla definía su propio `fmtMoney` (88 archivos lo
 * declaran y hay ~139 llamadas sueltas a toLocaleString("es-DO", …)), todas
 * asumiendo pesos y ninguna capaz de pintar un símbolo distinto. Este módulo
 * es el reemplazo único; las pantallas se van migrando por módulo, no de
 * golpe, así que `fmtMoney` de aquí devuelve EXACTAMENTE lo mismo que las
 * copias existentes ("1,234.56", "0.00" para lo que no sea número) para que
 * migrar una pantalla no cambie ni un píxel.
 *
 * Convención de la tasa (la misma de factcxcmayor.tasa y del backend
 * app/monedas.py): cuántas unidades de la moneda BASE vale UNA unidad de la
 * otra moneda. USD 1 = RD$ 61.50 -> tasa 61.50.
 *
 * Uso:
 *   import { fmtMoney, fmtMoneda, useMonedas } from "lib/moneda";
 *
 *   const { monedas, tasas, base, multimoneda } = useMonedas();
 *   fmtMoney(1234.5)              // "1,234.50"
 *   fmtMoneda(1234.5, "USD")      // "US$ 1,234.50"
 */
import { useState, useEffect, useCallback } from "react";
import { useApi } from "lib/api";

/**
 * El peso dominicano: lo que significa el 'P' de las columnas heredadas y la
 * base por defecto de un tenant nuevo. NO es lo mismo que "la moneda base del
 * tenant" — un tenant puede tener base USD y sus filas viejas seguir siendo
 * pesos. Espejo de CODIGO_DOP en app/monedas.py.
 */
export const CODIGO_DOP = "DOP";

/** Símbolos por defecto, para pintar antes de que responda el catálogo. */
const SIMBOLOS = { DOP: "RD$", USD: "US$", EUR: "€" };

/**
 * Número con separador de miles y 2 decimales, SIN símbolo.
 * Idéntico a los fmtMoney repartidos por las pantallas: cambiar esto cambia
 * el formato de todo el sistema.
 */
export const fmtMoney = (v, decimales = 2) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return (0).toFixed(decimales);
  return n.toLocaleString("es-DO", {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
};

/** Convierte a número tolerando comas de miles y vacíos. */
export const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

/**
 * Moneda del escritorio ('P'/'U') -> código ISO. Acepta códigos ya
 * normalizados para poder llamarla sobre filas mixtas. Vacío cae a DOP,
 * replicando el ifnull(moneda,'P') de todas las consultas heredadas.
 * Contraparte de normaliza_legacy() en app/monedas.py.
 *
 * Devuelve DOP, nunca "la base del tenant": en los datos heredados 'P'
 * significa pesos dominicanos.
 */
export const normalizaLegacy = (v) => {
  const s = String(v || "")
    .trim()
    .toUpperCase();
  if (s === "U" || s === "USD") return "USD";
  if (s === "P" || s === "DOP" || s === "") return CODIGO_DOP;
  return s.slice(0, 3);
};

/**
 * Símbolo de una moneda. `catalogo` es el array de useMonedas(); sin él cae
 * a la tabla por defecto y, si tampoco está, al propio código ("EUR 10.00").
 */
export const simbolo = (codigo, catalogo) => {
  const cod = normalizaLegacy(codigo);
  const fila = (catalogo || []).find((m) => m.codigo === cod);
  return fila?.simbolo || SIMBOLOS[cod] || cod;
};

/** Monto con símbolo delante: "RD$ 1,234.50". */
export const fmtMoneda = (v, codigo, catalogo) => {
  const cod = normalizaLegacy(codigo);
  const fila = (catalogo || []).find((m) => m.codigo === cod);
  return `${simbolo(cod, catalogo)} ${fmtMoney(v, fila?.decimales ?? 2)}`;
};

/** Código ISO -> 'P'/'U' para escribir en las columnas heredadas. */
export const aLegacy = (codigo) => (normalizaLegacy(codigo) === "USD" ? "U" : "P");

/** Etiqueta corta para selectores e impresos: "RD$" / "USD". */
export const monedaLabel = (codigo) => {
  const cod = normalizaLegacy(codigo);
  return cod === CODIGO_DOP ? "RD$" : cod;
};

/**
 * Formato de una TASA (no de un monto). Las tasas no siempre son números
 * grandes: con base USD, la tasa del peso es 0.016260, y con 2 decimales se
 * vería "0.02". Se muestran hasta 6 decimales, sin ceros de relleno.
 */
export const fmtTasa = (v) => {
  const n = toNum(v);
  if (n === 0) return "0";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 6 });
};

/**
 * La tasa inversa, para mostrarla junto a la directa. La convención de
 * almacenamiento es "cuántas unidades de la base vale UNA de la otra moneda";
 * cuando la base es USD eso da números como 0.016260 para el peso, y lo que
 * la gente reconoce es el inverso (61.50). 0 si no hay tasa.
 */
export const tasaInversa = (tasa) => {
  const t = toNum(tasa);
  return t > 0 ? 1 / t : 0;
};

/**
 * Monto expresado en la moneda base, a la tasa dada. Tasa <= 0 devuelve el
 * monto tal cual (moneda base, o fila histórica sin tasa utilizable).
 */
export const aBase = (monto, tasa) => {
  const t = toNum(tasa);
  const m = toNum(monto);
  return t > 0 ? Math.round(m * t * 100) / 100 : Math.round(m * 100) / 100;
};

/**
 * Precio guardado en la moneda BASE, expresado en la moneda del documento.
 *
 * Es la regla de ventas x mayor, conduces y cotizaciones para llevar
 * producto.precio / producto.mprecio a la moneda de la factura. Estaba
 * duplicada en 4 pantallas escrita como `moneda === "U" ? precio / tasa :
 * precio`, que da mal en cuanto la base deja de ser el peso: con base USD,
 * facturar en USD volvía a dividir un precio que ya estaba en dólares.
 *
 * `tasa` conserva su significado histórico: PESOS por UN dólar, sea cual sea
 * la base. Por eso la operación cambia de lado.
 *   base DOP + documento USD -> precio / tasa
 *   base USD + documento DOP -> precio * tasa
 *   documento en la misma moneda que la base -> sin conversión
 *
 * Espejo de convertir_desde_base() en app/monedas.py.
 */
export const precioEnMoneda = (precioBase, moneda, tasa, codigoBase = CODIGO_DOP) => {
  const p = toNum(precioBase);
  if (p <= 0) return 0;
  const destino = normalizaLegacy(moneda);
  const base = normalizaLegacy(codigoBase);
  if (destino === base) return Math.round(p * 100) / 100;
  const t = toNum(tasa);
  if (t <= 0) return Math.round(p * 100) / 100;
  if (base === CODIGO_DOP && destino === "USD") return Math.round((p / t) * 100) / 100;
  if (base === "USD" && destino === CODIGO_DOP) return Math.round(p * t * 100) / 100;
  return Math.round(p * 100) / 100;
};

/** Inversa de aBase(): monto de la base expresado en la otra moneda. */
export const desdeBase = (monto, tasa) => {
  const t = toNum(tasa);
  const m = toNum(monto);
  return t > 0 ? Math.round((m / t) * 100) / 100 : Math.round(m * 100) / 100;
};

/**
 * Efectivo contado en otra moneda, llevado a la base (cuadre de caja).
 *
 * El desglose de "otras monedas" es anterior al catálogo: la moneda se
 * tecleaba a mano ("US$", "EU$") y el total se multiplicaba siempre por la
 * tasa — correcto con base peso, incorrecto con base USD, donde el efectivo
 * en pesos hay que dividirlo. Si la moneda no se reconoce se conserva la
 * multiplicación histórica. Espejo de convertir_efectivo_a_base en
 * app/monedas.py.
 */
export const convertirEfectivoABase = (monto, moneda, tasa, codigoBase = CODIGO_DOP) => {
  const base = normalizaLegacy(codigoBase);
  const cod = normalizaLegacy(moneda);
  const conocidas = [CODIGO_DOP, "USD"];
  if (conocidas.includes(cod) && conocidas.includes(base)) {
    return precioEnMoneda(monto, base, tasa, cod);
  }
  const t = toNum(tasa);
  const m = toNum(monto);
  return t > 0 ? Math.round(m * t * 100) / 100 : Math.round(m * 100) / 100;
};

/**
 * ¿Alguna fila del reporte se emitió en una moneda distinta a la base?
 *
 * Los reportes de x mayor devuelven los importes YA convertidos a la base y
 * la moneda original de cada fila. La columna "Moneda" solo aporta cuando hay
 * mezcla: en un tenant que opera solo en pesos sería una columna con el mismo
 * valor repetido, así que se pinta únicamente si esto devuelve true.
 */
export const hayOtraMoneda = (filas, base) => {
  const cod = normalizaLegacy(base);
  return (filas || []).some((f) => normalizaLegacy(f?.moneda) !== cod);
};

/**
 * Catálogo de monedas y tasas vigentes del tenant.
 *
 * `multimoneda` es false cuando solo está habilitada la base: las pantallas
 * lo usan para NO pintar selector de moneda ni pedir tasa en los tenants que
 * operan solo en pesos, que hoy son la mayoría.
 *
 * `recargar()` refresca tras registrar una tasa o habilitar una moneda.
 */
export function useMonedas({ fecha } = {}) {
  const { post } = useApi();
  const [datos, setDatos] = useState({
    monedas: [],
    base: null,
    tasas: {},
    tasaminima: 0,
    multimoneda: false,
  });
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/monedas_listar", fecha ? { fecha } : {});
      if (ok) {
        setDatos({
          monedas: data.monedas || [],
          base: data.base || null,
          tasas: data.tasas || {},
          tasaminima: toNum(data.tasaminima),
          multimoneda: Boolean(data.multimoneda),
        });
      }
    } finally {
      setCargando(false);
    }
  }, [post, fecha]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  return { ...datos, cargando, recargar: cargar };
}

/**
 * Tasa vigente de una moneda: { tasa, fecha, usuario, vigente }.
 * `vigente` false = la tasa devuelta es de un día anterior (nadie registró la
 * de hoy); la UI lo señala en ámbar en vez de fallar.
 */
export function useTasa(codigo) {
  const { tasas, cargando } = useMonedas();
  const cod = normalizaLegacy(codigo);
  return {
    ...(tasas[cod] || { codigo: cod, tasa: 0, fecha: null, usuario: "", vigente: false }),
    cargando,
  };
}
